import { useState, useCallback } from 'react';

export interface ApiCallState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface UseApiCallReturn<T, TArgs extends unknown[]> {
  data: T | null;
  loading: boolean;
  error: string | null;
  execute: (...args: TArgs) => Promise<T | null>;
  reset: () => void;
  setData: (data: T | null) => void;
}

/**
 * Custom hook for handling API calls with loading and error states
 * @param apiFunction - The async API function to call
 * @param options - Configuration options
 * @returns State and execute function
 *
 * @example
 * const { data, loading, error, execute } = useApiCall(api.fetchTrip);
 *
 * useEffect(() => {
 *   execute(tripId);
 * }, [tripId]);
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error} />;
 * return <TripDisplay trip={data} />;
 */
export function useApiCall<T, TArgs extends unknown[] = []>(
  apiFunction: (...args: TArgs) => Promise<T>,
  options: {
    onSuccess?: (data: T) => void;
    onError?: (error: Error) => void;
    initialData?: T | null;
  } = {}
): UseApiCallReturn<T, TArgs> {
  const { onSuccess, onError, initialData = null } = options;

  const [state, setState] = useState<ApiCallState<T>>({
    data: initialData,
    loading: false,
    error: null,
  });

  const execute = useCallback(
    async (...args: TArgs): Promise<T | null> => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const result = await apiFunction(...args);
        setState({ data: result, loading: false, error: null });
        onSuccess?.(result);
        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setState(prev => ({ ...prev, loading: false, error: errorMessage }));
        onError?.(err instanceof Error ? err : new Error(errorMessage));
        return null;
      }
    },
    [apiFunction, onSuccess, onError]
  );

  const reset = useCallback(() => {
    setState({ data: initialData, loading: false, error: null });
  }, [initialData]);

  const setData = useCallback((data: T | null) => {
    setState(prev => ({ ...prev, data }));
  }, []);

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    execute,
    reset,
    setData,
  };
}

/**
 * Hook for API calls that execute immediately on mount
 * @param apiFunction - The async API function to call
 * @param args - Arguments to pass to the API function
 * @param options - Configuration options
 */
export function useApiCallOnMount<T, TArgs extends unknown[]>(
  apiFunction: (...args: TArgs) => Promise<T>,
  args: TArgs,
  options: {
    onSuccess?: (data: T) => void;
    onError?: (error: Error) => void;
    enabled?: boolean;
  } = {}
): Omit<UseApiCallReturn<T, TArgs>, 'execute'> & { refetch: () => Promise<T | null> } {
  const { enabled = true, ...restOptions } = options;
  const { execute, ...rest } = useApiCall(apiFunction, restOptions);

  // Execute on mount if enabled
  useState(() => {
    if (enabled) {
      execute(...args);
    }
  });

  return {
    ...rest,
    refetch: () => execute(...args),
  };
}

export default useApiCall;
